import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { BuddyUser } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { userFieldBlackList } from '@adornis/bh-matching-user/user-field-blacklist.js';
import type { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import '@adornis/buchungsheld-theme/components/tr-checkbox.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { exportDataView } from '@adornis/data-view/db/export-data-view.js';
import { View } from '@adornis/data-view/db/view.js';
import { FieldConfiguration } from '@adornis/field-management/field-configuration.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspectConfig } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { buildBuddyExportBuildOptions } from '../api/BuddyExportBuildOptions.js';
import { BuddyExportDataView } from '../db/BuddyExportDataView.js';

@customElement('buddy-dialog-prepare-export')
export class BuddyDialogPrepareExport extends TrDialog<void> {
  static override get element_name() {
    return 'buddy-dialog-prepare-export';
  }

  @property({ attribute: false }) dataOptions: Maybe<DataViewOptions<typeof BuddyUser>>;
  @property({ attribute: false }) timeframe: Maybe<Timeframe>;
  @property({ attribute: false }) onlyWithMatch: Maybe<boolean>;
  @property({ attribute: false }) userClass?: typeof BuddyUser;

  protected _exportAllFields = false;

  protected translator = new TranslationController<any>(this);

  protected async _export() {
    const timeframe = this.timeframe;
    if (!this.dataOptions || !this.userClass || !timeframe) return;

    const dataOptions: DataViewOptions<typeof BuddyUser> = { ...this.dataOptions, class: this.userClass };

    const fullOpt = this._exportAllFields
      ? buildBuddyExportBuildOptions(
          {
            ...dataOptions,
            customColumns: Object.values(this.userClass.fields)
              .filter(field => !userFieldBlackList.includes(field.name))
              .map(field => ({ fieldName: field.name })),
            view: new View({
              ...dataOptions.view,
              configuredColumns: Object.values(this.userClass.fields)
                .filter(field => !userFieldBlackList.includes(field.name))
                .map(field => new FieldConfiguration({ fieldName: field.name })),
              filter: dataOptions.view?.filter,
            }),
          },
          timeframe,
          this.onlyWithMatch ?? false,
        )
      : buildBuddyExportBuildOptions(
          {
            ...dataOptions,
            view: new View({
              ...dataOptions.view,
              configuredColumns: dataOptions.view?.configuredColumns?.map(col => new FieldConfiguration(col)),
              filter:
                (console.log(dataOptions.view?.filter),
                new AdornisFilter({ aspects: [...(dataOptions.view?.filter?.aspects ?? [])] })),
            }),
          },
          timeframe,
          this.onlyWithMatch ?? false,
        );

    if (!fullOpt.dataViewOptions.customColumns.find(customCol => customCol.fieldName === 'matrikelNummer')) {
      fullOpt.dataViewOptions.customColumns.push({
        fieldName: 'matrikelNummer',
        renderItem: userEntity => {
          const matrikelNummer = (userEntity as { matrikelNummer?: string }).matrikelNummer;

          return matrikelNummer
            ? html` <x-text> ${this.translator.translate('enums', matrikelNummer)} </x-text> `
            : nothing;
        },
      });
    }

    if (!fullOpt.dataViewOptions.view?.filter?.aspects.find(a => a.fieldName === 'timeframeID'))
      fullOpt.dataViewOptions.view?.filter?.aspects.push(
        new (StringAspectConfig('equals').aspectConstructor as any)({
          mode: 'equals',
          value: timeframe._id,
          fieldName: 'timeframeID',
        }),
      );

    try {
      await TrDialog.waitFor(exportDataView(fullOpt.dataViewOptions, { exportClass: BuddyExportDataView }));
      await XSnackbar.show(this._translation.translate('buddy', 'exportSuccess' as any));
    } catch (err) {
      console.error(err);
      await TrDialog.alert(this._translation.translate('buddy', 'exportFailed' as any));
    } finally {
      fullOpt.dataViewOptions.view?.filter?.aspects.pop(); // ToDo: Simple fix while same VarCursor from data-view is used
    }

    return this.close();
  }

  override content() {
    return html`
      <tr-flex space="lg">
        <tr-flex space="md">
          <x-h4> ${this.translator.translate('route', 'export')}: </x-h4>

          <tr-checkbox
            label="${this._translation.translate('buddy', 'allFields' as any)}"
            .value=${this._exportAllFields}
            @value-picked=${(e: ValueEvent<boolean>) => (this._exportAllFields = e.detail.value)}
          ></tr-checkbox>
        </tr-flex>

        <tr-flex crossaxis-center>
          <tr-button icon="upload" @click=${() => this._export()}>
            ${this.translator.translate('buddy', 'export')}
          </tr-button>
        </tr-flex>
      </tr-flex>
    `;
  }
}
